'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

const steps = [
  { step: '01', title: 'Submit Application', desc: 'Complete the online application form with your personal and academic details.' },
  { step: '02', title: 'Document Review', desc: 'Our admissions team reviews your application within 5 business days.' },
  { step: '03', title: 'Enrolment Offer', desc: 'Receive your conditional offer letter and select your payment plan.' },
  { step: '04', title: 'Begin Learning', desc: 'Access your student portal, AI assistant, and start your first module immediately.' },
];

export default function ApplySection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = ref?.current?.querySelectorAll('.section-reveal, .stagger-child');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="section-pad-lg relative overflow-hidden" id="apply" ref={ref}>
      {/* Background */}
      <div className="absolute inset-0 green-gradient" />
      <div className="absolute top-0 left-0 right-0 h-px opacity-20" style={{ background: 'linear-gradient(90deg, transparent, var(--accent), transparent)' }} />
      <div className="absolute bottom-0 left-0 right-0 h-px opacity-20" style={{ background: 'linear-gradient(90deg, transparent, var(--accent), transparent)' }} />

      <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        <div className="text-center mb-14 section-reveal">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="divider-gold" />
            <span className="label-caps text-accent/80">Begin Your Journey</span>
            <div className="divider-gold" />
          </div>
          <h2 className="font-serif text-section-title text-white mb-4">
            Your Career in Real Estate
            <br />
            <span className="italic text-gradient-gold">Starts With One Decision</span>
          </h2>
          <p className="font-sans text-base text-white/65 max-w-xl mx-auto leading-relaxed">
            Applications for the 2026 intake are now open. Limited places are available for selected programmes.
          </p>
        </div>

        {/* Process steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
          {steps?.map((s, i) => (
            <div
              key={`step-${s?.step}`}
              className="relative stagger-child"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {i < steps?.length - 1 && (
                <div className="hidden lg:block absolute top-6 left-full w-full h-px border-t border-dashed border-white/15 z-0" />
              )}
              <div className="glass-luxury-dark rounded-xl p-6 relative z-10 hover:bg-white/12 transition-colors duration-300">
                <span className="font-serif font-bold text-3xl text-accent/40 block mb-3">{s?.step}</span>
                <h4 className="font-sans font-semibold text-sm text-white mb-2">{s?.title}</h4>
                <p className="font-sans text-xs text-white/55 leading-relaxed">{s?.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link href="/apply" className="btn-gold text-base px-10 py-4 rounded-xl font-semibold">
            Apply for 2026 Intake
            <ArrowRight size={18} />
          </Link>
          <Link href="/course-marketplace" className="btn-outline-white text-base px-10 py-4 rounded-xl font-semibold">
            Browse All Programmes
          </Link>
        </div>

        {/* Reassurances */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-10">
          {[
            'No application fee',
            'Decision within 5 business days',
            'Flexible payment plans',
            'Skills development pathways available',
          ]?.map((r) => (
            <div key={`reassure-${r}`} className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-accent" />
              <span className="font-sans text-sm text-white/60">{r}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}